import { Flex, Image, SimpleGrid, useColorMode } from '@chakra-ui/react';

import { KeyboardEvent, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getLoginFormPagePath } from '../../App';
import Button from '../../components/button';
import Heading from '../../components/heading';
import Layout from '../../components/layout';
import StepHeader from '../../components/step-header';
import Text from '../../components/text';
import { useSession } from '../../hooks/useSession';
import Logos from '../../images/logos';
import { TBookAbbrvs } from '../../types';
import { IBookSelectOption } from './select-root';

export interface SelectSportsbookProps {
    currentState: string;
    stateLabel?: string;
    selectedBookOption?: IBookSelectOption;
    bookOptions: IBookSelectOption[];
    setSelectedBookOption: (bookOption: IBookSelectOption) => void;
}

interface BookCardProps {
    bookOption: IBookSelectOption;
    isSelected: boolean;
    colorMode: 'light' | 'dark';
    onSelect: (bookOption: IBookSelectOption) => void;
}

function BookCard({ bookOption, isSelected, colorMode, onSelect }: BookCardProps) {
    const isLight = colorMode === 'light';
    const { isDisabled, disabledText } = bookOption;

    const handleSelect = () => {
        if (!isDisabled) onSelect(bookOption);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (isDisabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(bookOption);
        }
    };

    return (
        <Flex
            role="button"
            aria-pressed={isSelected}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : 0}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
            direction="column"
            align="center"
            justify="center"
            gap="12px"
            position="relative"
            minH="120px"
            p="16px"
            borderRadius={14}
            borderWidth={isSelected ? 2 : 1}
            borderStyle="solid"
            borderColor={
                isSelected
                    ? 'primary'
                    : isLight
                    ? 'border'
                    : '#0E1A56'
            }
            bg={isLight ? 'white' : 'darkNavy'}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            opacity={isDisabled ? 0.4 : 1}
            transition="border-color 0.15s ease, transform 0.15s ease"
            _hover={isDisabled ? {} : { borderColor: 'primary', transform: 'translateY(-2px)' }}
            _focusVisible={{ outline: '2px solid', outlineColor: 'primary', outlineOffset: '2px' }}
        >
            <Image
                src={Logos[colorMode][bookOption.value as TBookAbbrvs]}
                alt={bookOption.label}
                maxH="44px"
                maxW="70%"
                objectFit="contain"
            />
            {isDisabled && disabledText && (
                <Text size="xs" opacity={0.8} textAlign="center">
                    {disabledText}
                </Text>
            )}
        </Flex>
    );
}

export default function SelectSportsbook(props: SelectSportsbookProps) {
    const {
        currentState,
        stateLabel,
        selectedBookOption,
        bookOptions,
        setSelectedBookOption,
    } = props;
    const { push } = useHistory();
    const session = useSession();
    const { colorMode, toggleColorMode } = useColorMode();

    const displayState = stateLabel || currentState || 'your area';

    const goToTheLoginPage = useCallback(() => {
        push(
            getLoginFormPagePath(
                session?.cid!,
                selectedBookOption?.bookRegionId!
            )
        );
    }, [selectedBookOption]);

    useEffect(() => {
        if (
            session?.uiMode &&
            session?.uiMode != 'system' &&
            session?.uiMode != colorMode
        )
            toggleColorMode();
    }, []);

    //auto scroll to top of page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Layout
            colorMode={colorMode}
            alert={session?.user?.isSandbox ? 'SANDBOX MODE' : ''}
            stepHeader={<StepHeader step={3} label="Platform" />}
            buttons={
                <Button
                    onClick={goToTheLoginPage}
                    isDisabled={!selectedBookOption}
                    w="full"
                >
                    Continue
                </Button>
            }
            hasBackButton={session?.user?.backButton}
        >
            <Flex direction="column" align="flex-start" gap="24px" w="full">
                {session?.logoUrl && (
                    <Image
                        alignSelf="center"
                        maxH="48px"
                        maxW="160px"
                        objectFit="contain"
                        src={session?.logoUrl}
                        alt={session?.user?.name || ''}
                    />
                )}

                <Flex direction="column" align="center" gap="6px" w="full">
                    <Heading as="h1" fontSize="26px" textAlign="center">
                        Choose your platform
                    </Heading>
                    <Text size="sm" opacity={0.75} textAlign="center">
                        Pick from platforms available in {displayState}
                    </Text>
                </Flex>

                {bookOptions.length > 0 ? (
                    <SimpleGrid
                        columns={2}
                        spacing="16px"
                        w="full"
                        mb="40px"
                        // Cap the grid so a long book list scrolls inside the
                        // page instead of stretching it without bound. Short
                        // lists sit at natural height (maxH only caps).
                        maxH="52vh"
                        overflowY="auto"
                        // Interior breathing room so the selected border, focus
                        // ring (outline + 2px offset), and hover lift aren't
                        // clipped by overflow; negative mx keeps cards aligned
                        // with the heading (sits within the Layout's padding).
                        px="8px"
                        pt="8px"
                        pb="8px"
                        mx="-8px"
                    >
                        {bookOptions.map((bookOption) => (
                            <BookCard
                                key={bookOption.value}
                                bookOption={bookOption}
                                isSelected={
                                    selectedBookOption?.value ===
                                    bookOption.value
                                }
                                colorMode={colorMode}
                                onSelect={setSelectedBookOption}
                            />
                        ))}
                    </SimpleGrid>
                ) : (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        gap="8px"
                        w="full"
                        py="48px"
                        mb="40px"
                        textAlign="center"
                    >
                        <Text fontWeight="600">
                            No platforms available in {displayState}
                        </Text>
                        <Text size="sm" opacity={0.7}>
                            Try a different location, or check back soon.
                        </Text>
                    </Flex>
                )}
            </Flex>
        </Layout>
    );
}
